<script setup lang="ts">
	import { ref, onMounted, computed, watch, nextTick } from "vue";
	import { useDisplay } from "vuetify";
	import { getPacksWithFilters, cancelAnalysis } from "./api";
	import api from "@/api";
	import type { PackWithExecuting, AnalysisStatusResponseData } from "./types";
	import SeanIcon from "@/components/icons/SeanIcon.vue";
	import { useToast } from "vue-toastification";
	import Dialog from "@/components/Dialog.vue";
	import { useContactDebugFlags } from "@/helpers/contactDebug";
	import { logError, logWarn } from "@/helpers/logError";
	import { useLuckyReportQuota } from "@/components/scribe/composables/useLuckyReportQuota";
	import { useCurrentUserRole } from "@/composables/useCurrentUserRole";
	import { useScribeStore } from "@/stores/scribe";
	import ScribeAccessArchivedNotice from "./ScribeAccessArchivedNotice.vue";
	import { useScribeAccess } from "@/composables/useScribeAccess";
	import { hasIntegration, tooltipTextFor } from "./utils/packBadges";
	import { couldNot } from "@/helpers/userMessages";
	import { CANCELLING_LABEL, isCancellingAnalysisStatus } from "./utils/analysisStatus";

	const display = useDisplay();

	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			type: "project" | "conversation";
			id: string;
			disabled?: boolean;
			mediaProcessing?: boolean;
			loadingPackId?: string | null;
			runningAnalysis?: AnalysisStatusResponseData;
			mediaWarnings?: string[];
			isExternalEditor?: boolean;
		}>(),
		{
			disabled: false,
			mediaProcessing: false,
			loadingPackId: null,
			runningAnalysis: undefined,
			mediaWarnings: () => [],
			isExternalEditor: false
		}
	);

	const emit = defineEmits<{
		"update:modelValue": [value: boolean];
		"pack-selected": [pack: PackWithExecuting];
	}>();

	const toast = useToast();
	const scribeStore = useScribeStore();
	const scribeAccess = useScribeAccess();
	const luckyQuota = useLuckyReportQuota();
	const debugFlags = useContactDebugFlags();
	const { canManageSparks, isLoading: isRoleLoading } = useCurrentUserRole();

	// Cheat code:
	// `window.__contactDebug.luckyCheat = true`
	const cheatEnabled = computed(() => !!debugFlags.value.luckyCheat);

	// Test options for media warnings
	const testOptions = computed(() => debugFlags.value.mediaWarningsTest || {});
	const showTestWarnings = computed(() => testOptions.value.enabled || false);
	const testWarningType = computed(() => testOptions.value.type || "both"); // 'processing', 'selection', 'both'

	const packs = ref<PackWithExecuting[]>([]);
	const loading = ref(false);
	const loadingMore = ref(false);
	const nextCursor = ref<string | null>(null);
	const hasMore = computed(() => !!nextCursor.value);
	const scrollContainer = ref<HTMLElement | null>(null);
	// LSP-18527: the scroll hint is two separate decisions — whether it occupies
	// space (isScrollable) and whether it is visible (atScrollEnd). See
	// checkScrollHint below for why collapsing them into one flag flickered.
	const isScrollable = ref(false);
	const atScrollEnd = ref(false);
	const cancelling = ref(false);
	const showCancelConfirm = ref(false);

	const isRealTimeAnalysis = computed(() => props.runningAnalysis?.isRealTime === true);

	// LSP-18409: on a cross-org shared project the report runs under the OWNER tenant, so the
	// picker must offer the OWNER's packs (fetched via the server proxy), not the caller's own.
	const isOwnerTenantPacks = computed(() => props.isExternalEditor && props.type === "project");

	// Same page shape (`{ data, nextCursor }`) whichever tenant supplies the packs.
	const fetchPackPage = (cursor?: string) => {
		if (isOwnerTenantPacks.value) {
			const params = new URLSearchParams({ limit: "15", sortBy: "position", sortOrder: "asc", hasRealTimeMedia: "false" });
			if (cursor) params.set("cursor", cursor);
			return api.getProjectPacks(props.id, params.toString());
		}
		return getPacksWithFilters({ cursor, limit: 15, sortBy: "position", sortOrder: "asc", hasRealTimeMedia: false });
	};

	const dedupePacks = (items: PackWithExecuting[]) => {
		const seen = new Set<string>();
		return items.filter((pack) => {
			if (seen.has(pack.id)) return false;
			seen.add(pack.id);
			return true;
		});
	};

	// LSP-20160: the popup's "is cancelling" predicate — this popup's request is in flight, or the
	// analysis is already CANCELLING. While true, "Cancel Report" is disabled as "Cancelling…" and every
	// entry point below is a logged no-op, so a second cancel is never sent.
	const isCancelInProgress = computed(() => cancelling.value || isCancellingAnalysisStatus(props.runningAnalysis?.status));

	const warnIfCancelInProgress = (entryPoint: string): boolean => {
		if (!isCancelInProgress.value) return false;
		logWarn(`LuckyPackPopup: ignoring cancel ${entryPoint} because a cancel is already in progress`, {
			analysisId: props.runningAnalysis?.id,
			status: props.runningAnalysis?.status,
			reason: cancelling.value ? "request-in-flight" : "status-cancelling",
			type: props.type,
			id: props.id
		});
		return true;
	};

	const openCancelConfirm = () => {
		if (warnIfCancelInProgress("confirm open")) return;
		showCancelConfirm.value = true;
	};

	const handleCancel = async () => {
		if (!props.runningAnalysis || isRealTimeAnalysis.value) return;
		if (warnIfCancelInProgress("request")) return;
		// The prop is a computed over the shared store, which SSE events and the background poll can
		// empty while the cancel request is in flight. Capture the id up front so neither the success
		// path nor the catch dereferences a prop that has since gone undefined.
		const analysisId = props.runningAnalysis.id;
		showCancelConfirm.value = false;
		cancelling.value = true;
		try {
			await cancelAnalysis(analysisId);
			// LSP-16333: sync the shared store so reopening the popup doesn't show
			// "Running Your Spark..." for an analysis the user just cancelled.
			scribeStore.markAnalysisCancelling(analysisId);
			toast.success("Report cancellation requested.");
			emit("update:modelValue", false);
		} catch (e: any) {
			// LSP-19467: the caught value is a server/API error whose `message` is raw exception text
			// (an internal URL, a status-code string). The customer reads a written sentence; the raw
			// error keeps its diagnostic value in the console and Datadog.
			logError("LuckyPackPopup: cancel analysis request failed", e, { analysisId });
			toast.error(couldNot("cancel this report"));
		} finally {
			cancelling.value = false;
		}
	};

	// LSP-18527: ScribeFab mounts this popup unconditionally, so one instance serves every
	// open/close. A close-then-reopen before the in-flight request settles means TWO overlapping
	// loads, and the stale one must not be allowed to write anything - not the hint state, and not
	// the pack list either. Everything that writes shared state on an async continuation stamps its
	// generation on entry and drops out if a newer open has bumped it.
	let openGeneration = 0;

	// Both discard paths log: these are silent-failure gates on the exact code path this ticket
	// fixes, and CLAUDE.md requires a diagnostic at every fail-condition in that case. Open/reopen
	// is low-frequency, so this cannot spam the console the way a per-scroll-event log would.
	const consoleWarnSupersededOpen = (stage: string, generation: number) => {
		logWarn(`LuckyPackPopup: discarded a superseded open (${stage})`, {
			staleGeneration: generation,
			currentGeneration: openGeneration,
			type: props.type,
			id: props.id
		});
	};

	const loadPacks = async (append = false) => {
		if (append) {
			loadingMore.value = true;
		} else {
			loading.value = true;
			packs.value = [];
			nextCursor.value = null;
		}

		const generation = openGeneration;

		try {
			const previousCursor = nextCursor.value;
			const response = await fetchPackPage(append ? nextCursor.value || undefined : undefined);

			// Gating only the downstream measurement was a half-guard: this response would still
			// overwrite the reopened session's pack list. Drop it instead.
			if (generation !== openGeneration) {
				consoleWarnSupersededOpen(append ? "pack page append" : "pack list", generation);
				return;
			}

			const mappedPacks = response.data.map((p: any) => ({ ...p, executing: false }));
			const dedupedIncomingPacks = dedupePacks(mappedPacks);

			if (append) {
				const existingIds = new Set(packs.value.map((pack) => pack.id));
				const uniqueNewPacks = dedupedIncomingPacks.filter((pack) => !existingIds.has(pack.id));
				packs.value = [...packs.value, ...uniqueNewPacks];
				nextCursor.value =
					uniqueNewPacks.length === 0 || response.nextCursor === previousCursor ? null : response.nextCursor || null;
			} else {
				packs.value = dedupedIncomingPacks;
				nextCursor.value = response.nextCursor || null;
			}
		} catch (e: any) {
			console.error("Failed to load packs:", e);
		} finally {
			// Only the current open owns these flags; a superseded load clearing them would drop the
			// reopened session's spinner while its own request is still in flight.
			if (generation === openGeneration) {
				loading.value = false;
				loadingMore.value = false;
			}
		}
	};

	const onIntersect = (isIntersecting: boolean) => {
		if (isIntersecting && hasMore.value && !loadingMore.value) {
			loadPacks(true);
		}
	};

	const handlePackClick = async (pack: PackWithExecuting) => {
		if (props.loadingPackId || luckyQuota.isCheckingQuota.value) return;
		// LSP-18409: for a cross-org shared project the OWNER is billed and their cap is enforced
		// server-side (createProjectSpark), so the CALLER's quota must not block pack selection — this
		// mirrors runAnalysis, which already skips the caller-quota pre-check for project runs. A caller
		// whose own org is capped can still run an uncapped owner's report.
		if (!isOwnerTenantPacks.value) {
			await luckyQuota.refreshUsageCount();
			if (luckyQuota.hasReachedLimit.value) {
				return;
			}
		}
		emit("pack-selected", pack);
	};

	const handleClose = () => {
		emit("update:modelValue", false);
	};

	// LSP-18527: the "Scroll for more" row flickered on every scroll event on a Surface Go.
	//
	// The demonstrated cause is the show/hide test itself: a single flat 10px boundary driving a
	// v-if. scrollTop is not monotonic during momentum scrolling, and at a fractional device
	// pixel ratio it carries sub-pixel noise, so the boundary gets crossed repeatedly inside one
	// gesture - and because it gated a v-if, every crossing was a mount plus a layout reflow.
	// That is what reads as a glitch rather than a clean show/hide.
	//
	// Note on the CSS: max-height is `min(320px, calc(100svh - 240px - ...))`, so the viewport
	// term only participates below ~560px of viewport height. On the reported device it resolved
	// to the constant 320px, so the unit was NOT the trigger here - the earlier dvh spelling is
	// still worth replacing (it moves as browser chrome hides on short viewports, which a Surface
	// Go does reach at 200% scaling) but do not go looking for it as this bug's cause.
	//
	// The two flags keep layout out of the fine-grained decision, so no future change to this
	// card's box model can reintroduce the same class of bug:
	//   isScrollable - owns whether the row occupies space. Coarse and rarely-changing, with its
	//     own hysteresis because it gates a v-if and layout churn can straddle a bare cutoff.
	//   atScrollEnd  - owns visibility only, which has no layout effect at all, so the
	//     near-the-bottom test can never perturb the geometry the next evaluation reads.
	//     (In today's DOM the row is a sibling of v-card-text and sits outside .popup-body,
	//     whose height is pinned by its own max-height, so it does not feed back regardless.)
	//
	// Both decisions carry hysteresis, for the same reason: a bare threshold is crossed by layout
	// rounding and by non-monotonic scrollTop, which is the flicker this fix removes - isScrollable
	// especially, since it gates a v-if.
	//
	// Only atScrollEnd's thresholds are additionally CLAMPED to the available distance (see the
	// Math.min calls below). remainingScroll can never exceed maxScroll, so an unclamped exit
	// threshold is unreachable for short content and would latch the state permanently;
	// quartering/halving keeps enter < exit at every maxScroll, so that band is always live.
	// isScrollable's two constants are deliberately absolute pixel amounts, not fractions of
	// maxScroll: they answer "is there enough overflow to be worth a hint at all", which does not
	// scale with the content.
	const SCROLL_END_ENTER_PX = 8;
	const SCROLL_END_EXIT_PX = 32;
	const SCROLLABLE_ENTER_PX = 2;
	// Deliberately 1px rather than a sub-pixel value: below 1 the row could stay mounted for
	// content that settles at a fraction of a pixel of overflow, showing "Scroll for more" with
	// nothing reachable.
	const SCROLLABLE_EXIT_PX = 1;

	const checkScrollHint = () => {
		const element = scrollContainer.value;
		if (!element) {
			return;
		}
		// A sub-pixel gap is layout rounding, not content the user can reach.
		const maxScroll = element.scrollHeight - element.clientHeight;
		isScrollable.value = isScrollable.value ? maxScroll > SCROLLABLE_EXIT_PX : maxScroll > SCROLLABLE_ENTER_PX;
		if (!isScrollable.value) {
			// atScrollEnd is deliberately left alone. It is only read while the row is mounted, and
			// the moment the content is scrollable again this function recomputes it in the same
			// call. On the reachable path that recompute always clears a stale `true`: the browser
			// clamps scrollTop to 0 when the content stops overflowing, and at scrollTop 0
			// remainingScroll === maxScroll, which is strictly greater than
			// exitThreshold = min(32, maxScroll / 2) for every maxScroll > 0. Zeroing it here would
			// therefore only matter if something restored a large scrollTop without a measurement
			// in between, so it is not worth code no test can reach. The OPEN path is different and
			// does reset it, via resetScrollHint(), because that state really leaks across sessions.
			return;
		}
		const enterThreshold = Math.min(SCROLL_END_ENTER_PX, maxScroll / 4);
		const exitThreshold = Math.min(SCROLL_END_EXIT_PX, maxScroll / 2);
		const remainingScroll = maxScroll - element.scrollTop;
		atScrollEnd.value = atScrollEnd.value ? remainingScroll <= exitThreshold : remainingScroll <= enterThreshold;
	};

	// ScribeFab mounts this popup unconditionally (no v-if / :key), so the instance and both
	// hint refs survive every close and reopen. Reset them synchronously here - before the
	// awaits - or the previous session's latched state renders the hint over the loading
	// spinner until the packs resolve and checkScrollHint() self-corrects.
	const resetScrollHint = () => {
		isScrollable.value = false;
		atScrollEnd.value = false;
	};

	// Opening the popup always follows the same three steps. Kept in one place because the two
	// entry points drifted apart once already: onMounted was missing the final measurement, so a
	// consumer that mounted the popup already-open left the hint unmeasured until the first scroll.
	const openAndMeasure = async () => {
		const generation = ++openGeneration;
		resetScrollHint();
		await Promise.all([loadPacks(), luckyQuota.load()]);
		if (generation !== openGeneration) {
			consoleWarnSupersededOpen("measurement", generation);
			return;
		}
		await nextTick();
		// Re-check after the flush: a close/reopen landing during it would otherwise let this
		// continuation measure past the newer open's reset.
		if (generation !== openGeneration) {
			consoleWarnSupersededOpen("measurement after flush", generation);
			return;
		}
		checkScrollHint();
	};

	watch(
		() => props.modelValue,
		async (newVal) => {
			if (newVal) {
				await openAndMeasure();
			}
		}
	);

	onMounted(async () => {
		if (props.modelValue) {
			await openAndMeasure();
		}
	});
</script>

<template>
	<v-dialog
		:model-value="modelValue"
		@update:model-value="emit('update:modelValue', $event)"
		:width="display.smAndDown.value ? '90%' : '80%'"
		max-width="600"
		@keydown.esc="handleClose">
		<v-card class="pa-2 w-100 cursor-default" rounded="xl" elevation="8" @click.stop>
			<!-- Header -->
			<v-card-title class="pa-4 pb-2" style="white-space: normal !important">
				<div class="d-flex align-start">
					<SeanIcon :enable-tooltip="false" :is-running="false" :height="48" :width="48" class="flex-shrink-0" />
					<div class="d-flex flex-column ml-3" style="min-width: 0; flex: 1">
						<span class="text-h6">Sparky</span>
						<span class="text-body-2 text-medium-emphasis text-wrap"
							>My name is Sparky. I'm smarter than you think. I can help with lots of stuff, including AI.
							<a href="https://youtu.be/GDcjzvrV__Y" target="_blank">Learn more</a>.</span
						>
					</div>
					<v-btn icon variant="text" density="compact" size="small" class="ml-2 flex-shrink-0" @click="handleClose">
						<v-icon size="18">mdi-close</v-icon>
					</v-btn>
				</div>
			</v-card-title>

			<v-divider />

			<!-- LSP-18409: make it obvious these are the project owner's report prompts, not the caller's. -->
			<div v-if="isOwnerTenantPacks" class="d-flex align-start px-4 pt-3 text-body-2 text-medium-emphasis">
				<v-icon size="16" class="mr-2 mt-1 flex-shrink-0">mdi-information-outline</v-icon>
				<span>These are the project owner's report prompts. Your report runs on their project.</span>
			</div>

			<div v-if="luckyQuota.isUsageLimited.value && !isOwnerTenantPacks" class="quota-banner px-4 pt-3">
				<div class="text-body-2 mb-2">
					You have used {{ luckyQuota.cappedReportsUsed.value }} out of {{ luckyQuota.usageLimit }} free reports.
				</div>
				<v-progress-linear :model-value="luckyQuota.usageProgress.value" :height="8" color="primary" rounded />
			</div>

			<!-- Body -->
			<v-card-text class="popup-body-wrapper">
				<div ref="scrollContainer" class="popup-body" :style="{ overscrollBehavior: 'contain' }" @scroll="checkScrollHint">
					<div v-if="runningAnalysis" class="d-flex flex-column align-center justify-center py-6 text-center">
						<v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
						<div class="text-subtitle-1 font-weight-bold mb-2">Running Your Spark...</div>
						<div class="text-body-2 text-medium-emphasis mb-6 px-4">
							I'm running your Spark! You can leave this page and come back later! Your report will be available in the
							"Sparky's Reports" section.
						</div>
						<!-- LSP-20160: disabled while a cancel is in progress — an in-progress action, not
						     validity gating. Text instead of a spinner so "Cancelling…" stays readable. -->
						<v-btn
							v-if="!isRealTimeAnalysis"
							variant="outlined"
							color="primary"
							rounded="xl"
							:disabled="isCancelInProgress"
							@click="openCancelConfirm">
							{{ isCancelInProgress ? CANCELLING_LABEL : "Cancel Report" }}
						</v-btn>
					</div>

					<template v-else-if="luckyQuota.hasReachedLimit.value && !isOwnerTenantPacks">
						<div class="d-flex flex-column align-center justify-center py-6 text-center">
							<div class="text-subtitle-1 font-weight-bold mb-2">You have used all of your free reports.</div>
							<div class="text-body-2 text-medium-emphasis mb-6 px-4">Upgrade to keep running unlimited reports.</div>
							<v-btn
								color="primary"
								variant="flat"
								rounded="xl"
								:loading="luckyQuota.billingRedirecting.value"
								@click="luckyQuota.navigateToBilling">
								Upgrade in Billing
							</v-btn>
						</div>
					</template>

					<template v-else-if="disabled && !cheatEnabled">
						<div class="d-flex flex-column align-center justify-center py-6 text-center">
							<div v-if="mediaProcessing" class="text-body-2 text-medium-emphasis px-4">
								Your media is still processing. Please wait for it to be ready. Then I can run a Spark for you!
							</div>
							<div v-else class="text-body-2 text-medium-emphasis px-4">
								Upload some photos, videos, or PDFs to get started. Then I can run a Spark for you!
							</div>
						</div>
					</template>

					<template v-else>
						<!-- Loading -->
						<div v-if="loading && packs.length === 0" class="d-flex justify-center py-6">
							<v-progress-circular indeterminate color="primary" size="32" />
						</div>

						<!-- Empty -->
						<!-- LSP-17976: the pack list fails with Scribe's archived 403; explain it. -->
						<div v-else-if="packs.length === 0 && !loading && scribeAccess.isArchived.value" class="pa-2">
							<ScribeAccessArchivedNotice />
						</div>
						<div v-else-if="packs.length === 0 && !loading" class="text-center py-6">
							<div class="text-body-2 text-medium-emphasis">No reports available</div>
						</div>

						<!-- Pack list -->
						<div v-else class="d-flex flex-column ga-2">
							<!-- Media warnings -->
							<v-alert
								v-if="mediaWarnings.length > 0 || showTestWarnings"
								type="warning"
								variant="tonal"
								rounded="lg"
								density="compact"
								class="mb-2">
								<div class="text-body-2">
									<!-- LSP-19003: the interpolated space is load-bearing. Vue's default
									     whitespace: "condense" deletes a whitespace-only text node that spans a
									     newline, so a plain line break between </strong> and the warning text
									     rendered "Heads up!You've..." with no gap. -->
									<strong>Heads up!</strong>{{ " " }}
									<template v-if="mediaWarnings.length > 0">
										{{ mediaWarnings.join(" ") }}
									</template>
									<template v-else-if="showTestWarnings">
										<template v-if="testWarningType === 'processing'">
											I noticed 2 files are still processing, so I won't be able to include them in my analysis just
											yet.
										</template>
										<template v-else-if="testWarningType === 'selection'">
											You've selected 3 specific files for me to analyze, so I won't look at your other content.
										</template>
										<template v-else-if="testWarningType === 'both'">
											I noticed 2 files are still processing, so I won't be able to include them in my analysis just
											yet. You've selected 3 specific files for me to analyze, so I won't look at your other content.
										</template>
									</template>
								</div>
							</v-alert>

							<div v-for="pack in packs" :key="pack.id">
								<!-- LSP-19415: still a real button (not a plain div) with its accessible name pinned to
								     pack.name via aria-label - the pack picker's Playwright coverage (sparky-reports,
								     lucky-templates, spark-guides, role-user-permissions, media-playback journeys) keys off
								     `.pack-pill` and `getByRole('button', { name: pack.name, exact: true })`, and neither
								     survives becoming a non-button or gaining extra text in the accessible name. aria-label
								     replaces the button's whole accessible-name computation, which would otherwise silently
								     drop the description/INTEGRATION PROMPT text from screen readers - aria-describedby
								     keeps that content announced (as a description, alongside the name) instead. -->
								<v-btn
									class="pack-pill text-none"
									:aria-label="pack.name"
									:aria-describedby="`pack-details-${pack.id}`"
									:disabled="!!loadingPackId || disabled || luckyQuota.isCheckingQuota.value"
									:loading="loadingPackId === pack.id"
									variant="outlined"
									block
									@click="handlePackClick(pack)">
									<div class="d-flex flex-column w-100 text-left">
										<div class="d-flex align-start ga-2 mb-1">
											<span class="text-body-2 font-weight-medium flex-grow-1 min-width-0 pack-title">{{
												pack.name
											}}</span>
											<!-- Tooltip's activator is just the icon glyph, not the row - hovering the title
											     or the blank space next to it must not trigger it. Text is tooltipTextFor(pack),
											     not a fixed "community" string: LSP-15372 deliberately shows the integration text
											     instead when a pack is both a Community install and connects to an integration. -->
											<v-tooltip v-if="pack.ui?.isInstalled" location="top" :text="tooltipTextFor(pack)">
												<template #activator="{ props: tooltipProps }">
													<v-icon v-bind="tooltipProps" size="16" color="primary" class="flex-shrink-0"
														>mdi-earth</v-icon
													>
												</template>
											</v-tooltip>
										</div>
										<div :id="`pack-details-${pack.id}`">
											<div
												v-if="hasIntegration(pack)"
												class="integration-prompt-label text-caption font-weight-bold text-primary mb-1"
												data-test-id="integration-prompt-label">
												INTEGRATION PROMPT
											</div>
											<div v-if="pack.description" class="text-caption text-medium-emphasis description-clamp">
												{{ pack.description }}
											</div>
											<div v-else class="text-caption text-medium-emphasis font-italic">No description available</div>
										</div>
									</div>
								</v-btn>
							</div>

							<!-- Infinite scroll sentinel -->
							<div
								v-if="hasMore && !loading"
								v-intersect="{
									handler: onIntersect,
									options: { threshold: 0.1 }
								}"
								class="d-flex justify-center py-2">
								<v-progress-circular v-if="loadingMore" indeterminate color="primary" size="24" />
							</div>
						</div>
					</template>
				</div>
			</v-card-text>

			<!-- Loading overlay -->
			<div v-if="loadingPackId" class="popup-loading-overlay d-flex flex-column align-center justify-center">
				<v-progress-circular indeterminate color="primary" size="36" class="mb-2" />
				<div class="text-body-2 text-medium-emphasis">Loading report...</div>
			</div>

			<!-- Scroll hint -->
			<div v-if="isScrollable && !loadingPackId" class="scroll-hint text-center py-1" :class="{ 'scroll-hint--at-end': atScrollEnd }">
				<span class="text-caption text-medium-emphasis">
					Scroll for more
					<template v-if="!display.smAndDown.value && !isRoleLoading && canManageSparks">
						or visit the
						<router-link data-test-id="community-library-link" to="/sparks" @click="emit('update:modelValue', false)">
							Community Library
						</router-link>
					</template>
				</span>
			</div>
		</v-card>
	</v-dialog>

	<!-- Cancel confirmation dialog -->
	<Dialog
		:dialog-visible="showCancelConfirm"
		title="Cancel Report?"
		message="Are you sure you want to cancel this report? Any progress will be lost."
		agree-text="Cancel Report"
		cancel-text="Go Back"
		:agree-loading="cancelling"
		@agree="handleCancel"
		@cancel="showCancelConfirm = false" />
</template>

<style scoped lang="scss">
	.popup-body-wrapper {
		padding: 0 !important;
	}

	.popup-body {
		/* Fallback for browsers without small-viewport (svh) unit support */
		max-height: min(320px, calc(100vh - 240px - env(safe-area-inset-bottom, 0px)));
		/* LSP-18527: svh, not dvh. Both terms only participate below ~560px of viewport height,
		   so on the reported device this resolved to the constant 320px and the unit was not the
		   flicker's trigger. svh is still the right spelling: it is the stable small-viewport
		   height, so on a viewport short enough for the term to engage it does not move as
		   browser chrome hides. Removing a latent dependency, not fixing the reported bug. */
		max-height: min(320px, calc(100svh - 240px - env(safe-area-inset-bottom, 0px)));
		overflow-y: auto;
		padding: 16px;
		overscroll-behavior: contain;
	}

	// LSP-19415: match the tile look of the My Sparks list (MyTemplatesList.vue's .template-item)
	// so the same Sparks don't look unfamiliar between browsing and running a report. Still a
	// v-btn (not a plain div) - see the template comment on why the element and the `.pack-pill`
	// class stay put even though it no longer looks like a pill.
	.pack-pill {
		max-width: 100%;
		height: auto !important;
		padding: 12px 16px !important;
		border-radius: 12px !important;
		border-color: rgba(var(--v-theme-on-surface), 0.1) !important;
		transition:
			background-color 0.15s ease,
			box-shadow 0.15s ease;
	}

	.pack-pill:hover:not(:disabled) {
		box-shadow: 0 1px 4px rgba(var(--v-theme-on-surface), 0.06);
	}

	.pack-pill :deep(.v-btn__content) {
		white-space: normal;
		word-break: break-word;
		max-width: 100%;
		width: 100%;
		display: block;
		text-transform: none;
	}

	.pack-title {
		word-break: break-word;
	}

	.min-width-0 {
		min-width: 0 !important;
	}

	.description-clamp {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.integration-prompt-label {
		letter-spacing: 0.5px;
		line-height: 1;
	}

	.popup-loading-overlay {
		position: absolute;
		inset: 0;
		z-index: 1;
		background: rgba(255, 255, 255, 0.85);
		border-radius: inherit;
	}

	.cursor-default {
		cursor: default !important;
	}

	.scroll-hint {
		flex-shrink: 0;
		border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	}

	/* LSP-18527: hide without unmounting. `visibility` keeps the row's layout box identical in
	   both states, so toggling it cannot shift the card or the content the user is reading
	   mid-scroll. `display: none` or a v-if would reflow on every toggle. */
	.scroll-hint--at-end {
		visibility: hidden;
	}

	.text-wrap {
		word-break: break-word;
	}

	.quota-banner {
		border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	}
</style>
